import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AlertTriangle, MapPin, Mail } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <div className="flex flex-col sm:flex-row sm:gap-6 text-gray-600">
            <p className="text-lg">Effective Date: 03 February 2026</p>
            <p className="text-lg">Last Updated: 03 February 2026</p>
          </div>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
            <div>
              <h3 className="font-bold text-amber-800 mb-1">Important Medical Disclaimer</h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                <strong>VITRA is NOT a medical device.</strong> This platform does not diagnose, treat, cure, or prevent any medical condition or disease. The information provided through our platform is for informational purposes only and should not be considered as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. <strong>In case of a medical emergency, immediately call your local emergency services.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-8">
          
          {/* 1. Introduction and Acceptance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction and Acceptance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and VITRA ("Company", "we", "us", or "our") governing your access to and use of the VITRA application (the "App") and all related services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              By downloading, installing, accessing, or using the App, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use the App.
            </p>
          </section>

          {/* 2. Description of Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              VITRA provides a health tracking and remote patient monitoring platform that enables:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Recording and tracking health readings/vitals.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Manual entry of health data and notes</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Mood and feelings tracking (if enabled)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Medication tracking and reminders</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Emergency contact management</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Integration with connected devices. </span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Access clinical dashboards and reports. </span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span> Communicate with healthcare providers/clinicians</span>
              </li>
            </ul>
            <p className="text-gray-600 text-sm italic">
              Additional features: if any additional features are added in the future, they will be added to this list.
            </p>
          </section>

          {/* 3. Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To use the App, you must:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Be at least 18 years of age, or the age of legal majority in your jurisdiction</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Have the legal capacity to enter into a binding agreement</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Not be prohibited from using the App under applicable laws</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Provide accurate and complete information</span>
              </li>
            </ul>
            <div className="bg-blue-50 rounded-lg p-4 mt-4 border border-blue-100">
              <p className="text-blue-800 text-sm">
                <strong>Minors:</strong> If you are under the required age, you may only use the App under the supervision of a parent or legal guardian who agrees to be bound by these Terms on your behalf. We may request proof of guardian authority as required.
              </p>
            </div>
          </section>

          {/* 4. Account Registration and Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Account Registration and Security</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">5.1</span>
                  Account Creation
                </h3>
                <ul className="space-y-2 text-gray-700 ml-10">
                  <li>• You may be required to create an account to access certain features</li>
                  <li>• You agree to provide accurate, current, and complete information</li>
                  <li>• You must update your information promptly if it changes</li>
                  <li>• We reserve the right to verify your identity and information</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">5.2</span>
                  Account Security
                </h3>
                <ul className="space-y-2 text-gray-700 ml-10">
                  <li>• You are responsible for maintaining the confidentiality of your credentials</li>
                  <li>• You must use a strong password and keep it secure</li>
                  <li>• You must notify us immediately of any unauthorized access</li>
                  <li>• You are responsible for all activities under your account</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 5. User Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using the App, you agree to:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Use the App only for lawful purposes and in accordance with these Terms</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Provide accurate health information</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Not rely solely on the App for medical decisions or emergencies</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Consult qualified healthcare professionals for medical advice</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Comply with all applicable laws and regulations</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Keep the App updated to the latest version</span>
              </li>
            </ul>
          </section>

          {/* 6. Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Prohibited Activities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree NOT to:
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-red-500 pl-4 bg-red-50 p-4 rounded-r-lg">
                <ul className="space-y-2 text-red-800 text-sm">
                  <li>• Violate any applicable laws or regulations</li>
                  <li>• Impersonate another person or provide false information</li>
                  <li>• Attempt to gain unauthorized access to our systems</li>
                  <li>• Interfere with the proper functioning of the App</li>
                  <li>• Transmit viruses, malware, or harmful code</li>
                  <li>• Reverse engineer, decompile, or disassemble the App</li>
                  <li>• Use the App for unauthorized commercial purposes</li>
                  <li>• Scrape, data mine, or use automated data collection</li>
                  <li>• Copy, distribute, or create derivative works without permission</li>
                  <li>• Circumvent security measures or authentication</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 7. Medical Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Medical Disclaimer</h2>
            
            <div className="bg-amber-50 rounded-lg p-6 border border-amber-200 mb-6">
              <h3 className="font-bold text-amber-900 mb-3">IMPORTANT: Please Read Carefully</h3>
              <div className="space-y-4 text-amber-800">
                <p className="leading-relaxed">
                  <strong>VITRA is NOT a medical device and does not diagnose, treat, cure, or prevent any medical condition or disease.</strong>
                </p>
                <p className="leading-relaxed">
                  The App is designed for health data tracking and informational purposes only. The information provided through the App is not intended to be a substitute for professional medical advice, diagnosis, or treatment.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
                <div>
                  <h4 className="font-semibold text-gray-900">Not a Substitute for Professional Care</h4>
                  <p className="text-gray-700 text-sm">Always consult qualified healthcare professionals for medical advice, diagnosis, and treatment.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
                <div>
                  <h4 className="font-semibold text-gray-900">Emergency Situations</h4>
                  <p className="text-gray-700 text-sm">The App is NOT intended for medical emergencies. If you are experiencing a medical emergency, immediately call your local emergency services (e.g., 911, 997) or go to the nearest emergency room.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
                <div>
                  <h4 className="font-semibold text-gray-900">Data Accuracy</h4>
                  <p className="text-gray-700 text-sm">While we strive for accuracy, we do not guarantee that health data displayed is error-free. You are responsible for verifying the accuracy of your health information.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
                <div>
                  <h4 className="font-semibold text-gray-900">No Warranty of Health Outcomes</h4>
                  <p className="text-gray-700 text-sm">We make no guarantees regarding health outcomes from using the App.</p>
                </div>
              </div>
            </div>
          </section>

          {/* 8. Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">9.1</span>
                  Our Intellectual Property
                </h3>
                <p className="text-gray-700 ml-10">
                  The App, including all content, features, functionality, software, text, graphics, logos, and data, is owned by VITRA or our licensors and is protected by copyright, trademark, and other intellectual property laws. All rights not expressly granted are reserved.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">9.2</span>
                  Limited License
                </h3>
                <p className="text-gray-700 ml-10 mb-3">
                  We grant you a limited, non-exclusive, non-transferable, revocable license to use the App for personal, non-commercial purposes in accordance with these Terms.
                </p>
                <p className="text-gray-700 ml-10">This license does not include the right to modify, copy, sell, or create derivative works.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">9.3</span>
                  User Content
                </h3>
                <p className="text-gray-700 ml-10">
                  You retain ownership of data you submit. By submitting data, you grant us a license to use, process, and store it solely for providing the App's services to you.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">9.4</span>
                  Trademarks
                </h3>
                <p className="text-gray-700 ml-10">
                  "VITRA" and related logos are trademarks of VITRA. You may not use these without our prior written consent.
                </p>
              </div>
            </div>
          </section>

          {/* 9. Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Services and Integrations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The App may integrate with or link to third-party services, including:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Device integrations and connected health devices</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Health data platforms</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Cloud and hosting services</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Push notification services</span>
              </li>
            </ul>
            <div className="bg-gray-100 rounded-lg p-4">
              <p className="text-gray-700 text-sm">
                We are not responsible for the content, privacy practices, or actions of third-party services. Your use of such services is governed by their respective terms and policies.
              </p>
            </div>
          </section>

          {/* 10. Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-3">12.1 Termination by You</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• You may stop using the App at any time</li>
                  <li>• You may delete your account through in-app settings or by contacting support</li>
                  <li>• Termination does not relieve you of prior obligations</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-3">12.2 Termination by Us</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• We may suspend or terminate your access at any time</li>
                  <li>• Reasons include violation of Terms, fraud, or legal requirements</li>
                  <li>• We will provide notice when reasonably possible</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 border-l-4 border-gray-400 pl-4">
              <h4 className="font-semibold text-gray-900 mb-2">12.3 Effect of Termination</h4>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Your right to access the App will cease</li>
                <li>• Data will be handled in accordance with our Privacy Policy</li>
                <li>• Provisions that should survive termination will remain in effect</li>
              </ul>
            </div>
          </section>

          {/* 11. Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Disclaimers</h2>
            
            <div className="bg-gray-800 text-white rounded-lg p-6">
              <p className="leading-relaxed mb-4 uppercase text-sm font-semibold text-gray-300">
                Please Read Carefully
              </p>
              <div className="space-y-4 text-sm">
                <p className="leading-relaxed">
                  THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="leading-relaxed">
                  WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                </p>
                <p className="leading-relaxed text-amber-300">
                  VITRA IS NOT A MEDICAL DEVICE AND DOES NOT PROVIDE MEDICAL ADVICE. THE APP IS NOT INTENDED TO BE A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT.
                </p>
              </div>
            </div>
          </section>

          {/* 12. Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
            
            <div className="bg-gray-100 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed mb-4 text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <ul className="space-y-3 text-gray-700 text-sm">
                <li className="flex gap-3">
                  <span className="text-gray-500 font-bold">1.</span>
                  <span>VITRA SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR HEALTH OUTCOMES.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-gray-500 font-bold">2.</span>
                  <span>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR WHICHEVER IS GREATER.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-gray-500 font-bold">3.</span>
                  <span>WE ARE NOT LIABLE FOR DAMAGES ARISING FROM YOUR RELIANCE ON INFORMATION PROVIDED THROUGH THE APP OR YOUR FAILURE TO SEEK APPROPRIATE MEDICAL CARE.</span>
                </li>
              </ul>
              <p className="text-gray-600 text-sm mt-4 italic">
                Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.
              </p>
            </div>
          </section>

          {/* 13. Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless VITRA from and against any claims, liabilities, damages, losses, costs, and expenses arising out of or related to:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Your use or misuse of the App</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Your violation of these Terms</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Your violation of applicable laws or third-party rights</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">•</span>
                <span>Any content you submit through the App</span>
              </li>
            </ul>
          </section>

        

          {/* 14. Updates to These Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Updates to These Terms</h2>
            <div className="bg-gray-800 text-white rounded-lg p-6">
              <p className="leading-relaxed mb-4">
                We may update these Terms from time to time. We will notify you of material changes through the App or other appropriate means, and we will update the "Last Updated" date above.
              </p>
              <p className="text-teal-300 font-semibold">
                Your continued use of the App after any changes indicates your acceptance of the updated Terms.
              </p>
            </div>
          </section>

          {/* 15. Complaints */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have a question or complaint regarding these Terms or our services, contact us using the details provided above. You may also lodge a complaint with the relevant competent authority via its official channels.
            </p>
          </section>

            {/* 16. Company Contact Details */}
            <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Company Contact Details</h2>
            <div className="bg-gray-50 rounded-lg p-6 space-y-3">
              <div>
                <p className="font-semibold text-gray-900 text-lg">VITRA</p>
                <span>Developer Name: ADVANCED COMMUNICATIONS AND ELECTRONICS SYSTEMS COMPANY (ACES)</span>
                <div className="mt-3 space-y-2 text-gray-700">
                  <p className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" strokeWidth={2} aria-hidden="true" />
                    <span>Address: KSA</span>
                    
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-teal-600" strokeWidth={2} aria-hidden="true" />
                    <span>Privacy Email: androidadmin@aces-co.com · appleadmin@aces-co.com</span>
                  </p>
                  {/* <p className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Phone:</span>
                  </p> */}
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;

